/* eslint-disable */
/*
 * THE MOST COMMON ROUTE THROUGH THE SITE, for the Insights tab (2026-10-24). Pure: no database, no clock. THE SAME FILE LIVES IN THE FRONTEND (skydive_frontend src/insights/pathCore.ts), which the demo's
 * own backend uses to answer this over its own sessions; the two are kept equal by one fixture (insightsPath.fixture.json in each repo) and one test per side that reads it.
 *
 * What it is (Chris, 2026-10-24: "Greedy"): the most common way ONWARD. It starts at the page most visits begin on, then takes the most common next page AMONG THE VISITS THAT CAME THE SAME WAY (so every
 * percentage is a count of real visits, never a product of averages), never revisits a page, and stops when fewer than MIN_STEP_SESSIONS visits go on, or after MAX_STOPS stops. Visits that END on a stop are
 * not a reason to stop (2026-10-25, Chris: the red line on the canvas showed a long route while this one stopped at the first page, because most visits to a home page end there): they are counted and shown
 * as the share that leaves, and the route follows the visits that go on. Each step also keeps the other pages visits went to from the same point (`others`), so the card can show the alternatives.
 * It is the most common next step at each point, NOT a claim that any visit took the whole route.
 *
 * A "stop" is a page, or a page group: the server files a group's history under the group's name, and consecutive views of the same name are ONE stop (so wandering between a group's own pages is not a
 * move). How many pages a stop took (`run`) is kept so the tab can say what happens inside a group.
 */
export const MAX_STOPS = 5;
export const MIN_STEP_SESSIONS = 5;
export const OTHERS_SHOWN = 3;
/** a safety limit on the tree (one node per distinct route prefix): past it new routes are ignored, which can only make the chain shorter, never wrong */
export const MAX_TRIE_NODES = 200000;

/** the separator inside a route's key (a page's own name never holds it) */
export const ROUTE_SEP = '\u0001';
export interface RouteCount { n: number; ended: number; runSum: number }

interface TrieNode { n: number; ended: number; runSum: number; kids: Map<string, TrieNode> }
const newNode = (): TrieNode => ({ n: 0, ended: 0, runSum: 0, kids: new Map() });

export interface PathStep {
  page: string;
  /** visits that began at the first stop and followed the route this far (the first stop: every visit that began there) */
  reached: number;
  /** visits that ended on this stop having come exactly this way */
  ended: number;
  /** pages viewed per visit while on this stop (1 for a page; more for a group, whose own pages are one stop) */
  run: number;
  /** the other pages visits went to from the same point (not already on the route), most visited first, at most OTHERS_SHOWN: the alternatives to this stop (for the first stop: the other pages visits began on) */
  others: Array<{ page: string; reached: number }>;
  /** how far ahead of the runner-up this stop was chosen, as a share of the visits that were deciding (the first stop: of all visits) */
  lead: number;
}

export interface PathAnswer {
  /** visits with at least one page view (people's, ended) */
  visits: number;
  steps: PathStep[];
  /** why the route stops: "ended" (no visit went on from the last stop), "thin" (too few go on), "max", or "none" (no route: no visits, or none that go anywhere) */
  stop: 'ended' | 'thin' | 'max' | 'none';
}

export class PathAccumulator {
  private readonly root = newNode();
  private nodes = 1;
  private visits = 0;
  private capped = false;

  /** one visit's pages, in the order they were viewed (an empty name is skipped) */
  add(pages: string[]): void {
    const stops: Array<{ page: string; run: number }> = [];
    for (const page of pages) {
      if (!page) continue;
      const last = stops[stops.length - 1];
      if (last && last.page === page) last.run++;
      else stops.push({ page, run: 1 });
    }
    if (stops.length === 0) return;
    this.visits++;
    this.root.n++;
    let at = this.root;
    const depth = Math.min(stops.length, MAX_STOPS);
    for (let i = 0; i < depth; i++) {
      let kid = at.kids.get(stops[i].page);
      if (!kid) {
        if (this.nodes >= MAX_TRIE_NODES) { this.capped = true; return; }
        kid = newNode();
        at.kids.set(stops[i].page, kid);
        this.nodes++;
      }
      kid.n++;
      kid.runSum += stops[i].run;
      at = kid;
    }
    if (stops.length <= MAX_STOPS) at.ended++;
  }

  /** true when the tree hit its limit (the caller logs it) */
  get truncated(): boolean { return this.capped; }

  /**
   * The tree as a flat map (the route so far, its stops joined by ROUTE_SEP, to its counts), which two of these can be ADDED together: a day at a time can be kept and a range is the sum of its days.
   * `n` is how many visits went this way this far, `ended` how many ended here, `runSum` the pages viewed on this last stop across them. fromRoutes(routes()) answers exactly what this does.
   */
  routes(): Record<string, RouteCount> {
    const out: Record<string, RouteCount> = {};
    const walk = (node: TrieNode, prefix: string) => {
      for (const [page, kid] of Array.from(node.kids.entries())) {
        const key = prefix === '' ? page : prefix + ROUTE_SEP + page;
        out[key] = { n: kid.n, ended: kid.ended, runSum: kid.runSum };
        walk(kid, key);
      }
    };
    walk(this.root, '');

    return out;
  }

  static fromRoutes(routes: Record<string, RouteCount>): PathAccumulator {
    const acc = new PathAccumulator();
    for (const key of Object.keys(routes).sort((a, b) => a.split(ROUTE_SEP).length - b.split(ROUTE_SEP).length)) {
      const stops = key.split(ROUTE_SEP);
      let at = acc.root;
      for (let i = 0; i < stops.length - 1; i++) {
        const next = at.kids.get(stops[i]);
        if (!next) { at = null as unknown as TrieNode; break; }
        at = next;
      }
      if (!at) continue;                       // a route whose earlier stops are missing (a partial map): it cannot be placed, so it is left out
      const count = routes[key];
      const kid = newNode();
      kid.n = count.n; kid.ended = count.ended; kid.runSum = count.runSum;
      at.kids.set(stops[stops.length - 1], kid);
      acc.nodes++;
      if (stops.length === 1) { acc.root.n += count.n; acc.visits += count.n; }
    }

    return acc;
  }

  answer(): PathAnswer {
    const steps: PathStep[] = [];
    const chain = new Set<string>();
    let at = this.root;
    let stop: PathAnswer['stop'] | null = null;
    for (let i = 0; i < MAX_STOPS; i++) {
      let best: { page: string; kid: TrieNode } | null = null;
      let secondN = 0;
      const rest: Array<{ page: string; reached: number }> = [];
      for (const [page, kid] of Array.from(at.kids.entries())) {
        if (chain.has(page)) continue;
        if (!best || kid.n > best.kid.n || (kid.n === best.kid.n && page < best.page)) {
          if (best) { secondN = Math.max(secondN, best.kid.n); rest.push({ page: best.page, reached: best.kid.n }); }
          best = { page, kid };
        } else {
          secondN = Math.max(secondN, kid.n);
          rest.push({ page, reached: kid.n });
        }
      }
      if (!best) { stop = i === 0 ? 'none' : 'ended'; break; }
      if (best.kid.n < MIN_STEP_SESSIONS) { stop = i === 0 ? 'none' : 'thin'; break; }
      rest.sort((a, b) => b.reached - a.reached || (a.page < b.page ? -1 : 1));
      steps.push({ page: best.page, reached: best.kid.n, ended: best.kid.ended, run: best.kid.runSum / best.kid.n, others: rest.slice(0, OTHERS_SHOWN), lead: (best.kid.n - secondN) / Math.max(1, at.n) });
      chain.add(best.page);
      at = best.kid;
    }
    if (stop === null) stop = 'max';

    return { visits: this.visits, steps, stop: steps.length === 0 ? 'none' : stop };
  }
}
